import React, { useRef, useEffect } from 'react';

interface NavBackgroundProps {
  frozen?: boolean;
}

const NavBackground: React.FC<NavBackgroundProps> = ({ frozen = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  let animationFrameId: number;

  const dots = useRef<Array<{x: number; y: number; vx: number; vy: number}>>([]);

  const initDots = (width: number, height: number) => {
    // Only create 15 dots for performance
    dots.current = Array(15).fill(0).map(() => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3
    }));
  };

  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    // Update dots position
    dots.current.forEach(dot => {
      dot.x += dot.vx;
      dot.y += dot.vy;

      // Wrap around edges
      if (dot.x < 0) dot.x = width;
      if (dot.x > width) dot.x = 0;
      if (dot.y < 0) dot.y = height;
      if (dot.y > height) dot.y = 0;
    });

    // Draw connections
    ctx.strokeStyle = 'rgba(243, 238, 244, 0.1)';
    ctx.lineWidth = 1;

    dots.current.forEach((dot1, i) => {
      dots.current.slice(i + 1).forEach(dot2 => {
        const dx = dot1.x - dot2.x;
        const dy = dot1.y - dot2.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 100) {
          ctx.beginPath();
          ctx.moveTo(dot1.x, dot1.y);
          ctx.lineTo(dot2.x, dot2.y);
          ctx.stroke();
        }
      });
    });

    // Draw dots
    ctx.fillStyle = 'rgba(238, 244, 244, 0.2)';
    dots.current.forEach(dot => {
      ctx.beginPath();
      ctx.arc(dot.x, dot.y, 2, 0, Math.PI * 2);
      ctx.fill();
    });

    animationFrameId = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      initDots(canvas.width, canvas.height);
    };

    resizeCanvas();
    animate();

    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Control animation based on frozen prop
  useEffect(() => {
    if (frozen) {
      cancelAnimationFrame(animationFrameId);
    } else {
      animate();
    }
    // Cleanup on prop change
    return () => cancelAnimationFrame(animationFrameId);
    // eslint-disable-next-line
  }, [frozen]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0
      }}
    />
  );
};

export default NavBackground;
